import React, { useMemo } from 'react';
import {
  Sparkles,
  ClipboardList,
  Plus,
  PanelRight,
  Pencil,
  Settings,
  Trash2,
} from 'lucide-react';
import GroupedRowActionSheet, { ICON_PROPS } from '../ui/GroupedRowActionSheet';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

/**
 * Per-assessment row actions — config wrapper around GroupedRowActionSheet.
 * Presentation only; parent supplies handlers wired to existing assignment logic.
 */
export default function AssessmentRowActionsMenu({
  row,
  reportLoading,
  onReview,
  onSettings,
  onDelete,
  onGenerate,
  onAssign,
  onResource,
  onEdit,
  showAiBuilder = true,
}) {
  const { lockProps, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const title = row.price ? `$ ${row.name}` : row.name;

  const groupItems = useMemo(() => [
    {
      key: 'review',
      icon: <ClipboardList {...ICON_PROPS} />,
      label: 'Review questions',
      ...lockProps('action.assessment.review'),
    },
    {
      key: 'assign',
      icon: <Plus {...ICON_PROPS} />,
      label: 'Assign student',
      ...lockProps('action.assessment.assign'),
    },
    {
      key: 'resource',
      icon: <PanelRight {...ICON_PROPS} />,
      label: reportLoading ? 'Loading resource…' : 'Instructor resource',
      disabled: reportLoading,
      ...lockProps('action.assessment.resource'),
    },
  ], [reportLoading, lockProps]);

  const items = useMemo(() => [
    {
      key: 'edit',
      icon: <Pencil {...ICON_PROPS} />,
      label: 'Edit',
      ...lockProps('action.assessment.edit'),
    },
    {
      key: 'settings',
      icon: <Settings {...ICON_PROPS} />,
      label: 'Assessment settings',
      ...lockProps('action.assessment.settings'),
    },
  ], [lockProps]);

  const handleAction = (key) => {
    if (key === 'generate') onGenerate(row);
    else if (key === 'review') onReview(row);
    else if (key === 'assign') onAssign(row);
    else if (key === 'resource') onResource(row);
    else if (key === 'edit') onEdit(row);
    else if (key === 'settings') onSettings(row);
    else if (key === 'delete') onDelete(row);
  };

  return (
    <>
      <GroupedRowActionSheet
        entityLabel="Assessment"
        title={title}
        menuAriaLabel="Assessment actions"
        layoutKey={`${row.testId}-${reportLoading}-${showAiBuilder}`}
        primary={showAiBuilder ? {
          key: 'generate',
          icon: <Sparkles {...ICON_PROPS} />,
          label: 'Generate questions',
          ...lockProps('action.assessment.generateQuestion'),
        } : null}
        groupLabel="For this assessment"
        groupItems={groupItems}
        items={items}
        dangerItem={{
          key: 'delete',
          icon: <Trash2 {...ICON_PROPS} />,
          label: 'Delete',
          ...lockProps('action.assessment.delete'),
        }}
        onAction={handleAction}
      />
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </>
  );
}
